import { combineReducers } from 'redux';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

// Front
import LayoutReducer from './layouts/reducer';

import ForgetPasswordReducer from './auth/forgetpwd/reducer';
import ProfileReducer from './auth/profile/reducer';
import { configureStore } from '@reduxjs/toolkit';

import { authReducer, authApiSlice } from '../features/auth/index';
import SidebarSliceReducer from './slices/common/sidebarSlice';
import { productApiSlice } from 'features/products/redux/productApiSlice';
import { productReducer } from 'features/products/redux/productSlice';
import { masterDataApiSlice } from 'common/api/MasterdataApiSlice';
import { lovApiSlice } from 'common/api/lovApiSlice';
import { locationApiSlice } from 'features/common/location/redux/locationApiSlice';
import { organizationsApiSlice } from 'features/organizations/redux/organizationsApiSlice';
import { websiteApiSlice } from 'features/common/website/redux/websiteApiSlice';
import { priceListApiSlice } from 'features/common/pricelist/redux/priceListApiSlice';


const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  whitelist: ['auth', 'Layout', 'sidebarState', 'productApiSlice', 'products'], // Add reducers you want to persist
};

const rootReducer = combineReducers({
  auth: authReducer,
  Layout: LayoutReducer,
  ForgetPassword: ForgetPasswordReducer,
  Profile: ProfileReducer,
  sidebarState: SidebarSliceReducer, 
  products: productReducer,
  [authApiSlice.reducerPath]: authApiSlice.reducer,
  [productApiSlice.reducerPath]: productApiSlice.reducer,
  [masterDataApiSlice.reducerPath]: masterDataApiSlice.reducer,
  [lovApiSlice.reducerPath]: lovApiSlice.reducer,
  [locationApiSlice.reducerPath]: locationApiSlice.reducer,
  [organizationsApiSlice.reducerPath]: organizationsApiSlice.reducer,
  [websiteApiSlice.reducerPath]: websiteApiSlice.reducer,
  [priceListApiSlice.reducerPath]: priceListApiSlice.reducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(
      authApiSlice.middleware, 
      productApiSlice.middleware, 
      masterDataApiSlice.middleware,
      lovApiSlice.middleware,
      locationApiSlice.middleware,
      organizationsApiSlice.middleware,
      websiteApiSlice.middleware,
      priceListApiSlice.middleware
    ),
  devTools: {
    actionSanitizer: (action: any) => {
      // Sanitize LOV actions to prevent large payloads in DevTools
      if (action.type?.includes('lov') || action.type?.includes('GetListOfValues')) {
        return {
          ...action,
          payload: action.payload && typeof action.payload === 'object' && 'data' in action.payload
            ? { ...action.payload, data: `[${action.payload.data?.length || 0} items]` }
            : action.payload
        };
      }
      return action;
    },
    stateSanitizer: (state: any) => {
      // Sanitize LOV data in state to show only counts
      if (state.lovApiSlice?.queries) {
        const sanitizedQueries: any = {};
        Object.keys(state.lovApiSlice.queries).forEach(key => {
          const query = state.lovApiSlice.queries[key];
          if (query?.data?.data) {
            sanitizedQueries[key] = {
              ...query,
              data: {
                ...query.data,
                data: `[${query.data.data.length} items]`
              }
            };
          } else {
            sanitizedQueries[key] = query;
          }
        });
        return {
          ...state,
          lovApiSlice: {
            ...state.lovApiSlice,
            queries: sanitizedQueries
          }
        };
      }
      return state;
    },
    maxAge: 50, // Limit the number of actions stored
  },
});

export const persistor = persistStore(store);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

//purge redux store
// persistor.purge();

export default store;
